import React, { useState, useEffect, useContext } from 'react';
import { useUserAuth } from '../context/UserAuthContext';
import hello from '../assert/cloud-bannar.svg';
import { MyContext } from './Structure';
import { CiMobile3 } from "react-icons/ci";
import { useNavigate } from 'react-router-dom';
import { PiChartPieSliceDuotone, PiFilesDuotone, PiHardDriveDuotone, PiArrowUpRightBold, PiDownloadSimpleBold } from 'react-icons/pi';

function Home() {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { calculateTotalFileSize, fileImgCount, fileVideoCount, totalSize, totalSizePercent } = useContext(MyContext);

  const bytesToMB = (bytes) => {
    if (bytes === 0) return '0 MB';
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes.toFixed(2)} MB`;
  };

  const calculate = () => {
    setLoading(true);
    try {
      calculateTotalFileSize();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calculate();
  }, [user]);

  const StatsCard = ({ title, value, subtext, icon: Icon, colorClass, link }) => (
    <div
      onClick={() => link && navigate(link)}
      className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow group h-full cursor-pointer"
    >
      <div className="flex justify-between items-start mb-6">
        <div className={`p-4 rounded-xl ${colorClass} bg-opacity-10 shrink-0`}>
          <Icon className={`text-2xl ${colorClass.replace('bg-', 'text-')}`} />
        </div>
        <span className="text-gray-300 group-hover:text-blue-500 transition-colors">
          <PiArrowUpRightBold className="text-lg" />
        </span>
      </div>
      <div>
        <h3 className="text-[13px] font-semibold text-gray-400 mb-2 uppercase tracking-wide">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mb-2">{value}</p>
        <p className="text-[13px] text-gray-500 font-medium">
          {subtext}
        </p>
      </div>
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-100px)] bg-[#f8fafc] overflow-y-auto flex flex-col p-4 md:p-6 lg:p-8">
      {/* Hero Welcome Section */}
      <div className="relative w-full mb-6 lg:mb-8 overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-sm shrink-0">
        <div className="absolute top-0 right-0 w-full h-full bg-[url('https://dashboard.algolia.com/client-assets/c1c9361fe75370d1b156733e962f7214/514f2ec3798090c6df00dad1592c8166.svg')] bg-no-repeat bg-right-bottom opacity-[0.15] pointer-events-none scale-125"></div>

        <div className="relative z-10 p-6 md:p-10 lg:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f172a] mb-4 tracking-tight leading-[1.1]">
              Welcome back, <br className="hidden md:block" />
              <span className="text-[#80cde8] capitalize">
                {user?.displayName || 'Cloud User'}!
              </span>
            </h1>
            <p className="text-slate-500 text-base md:text-lg max-w-lg mb-6 leading-relaxed font-medium">
              Your storage looks healthy! Check what's happening on your dashboard today.
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <button
                onClick={() => navigate('/dashboard/myspace')}
                className="px-6 lg:px-8 py-3 bg-gray-700 text-white font-bold rounded-xl shadow-lg shadow-gray-200 hover:bg-gray-700 transition-all hover:-translate-y-0.5 active:scale-95 text-sm lg:text-base"
              >
                Upload Files
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="px-6 lg:px-8 py-3 bg-white text-slate-700 font-bold rounded-xl border border-slate-200 hover:bg-slate-50 transition-all text-sm lg:text-base"
              >
                Upgrade Plan
              </button>
            </div>
          </div>
          <div className="hidden lg:block shrink-0 relative mr-4">
            <img src={hello} alt="Welcome" className="w-[300px] xl:w-[350px] h-auto drop-shadow-2xl relative z-20" />
            <div className="absolute -inset-4 bg-blue-100/40 rounded-full blur-3xl -z-10"></div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 shrink-0 mb-6 lg:mb-8">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 animate-pulse min-h-[160px]">
              <div className="w-12 h-12 bg-gray-100 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/3 mb-3"></div>
              <div className="h-8 bg-gray-100 rounded w-2/3"></div>
            </div>
          ))
        ) : (
          <>
            <StatsCard
              title="Total Storage Size"
              value={bytesToMB(totalSize)}
              subtext="Accumulated size of all assets"
              icon={PiHardDriveDuotone}
              colorClass="bg-blue-500"
              link="/dashboard/myspace"
            />
            <StatsCard
              title="Files Inventory"
              value={`${fileImgCount + fileVideoCount} Total Items`}
              subtext={`${fileImgCount} Images • ${fileVideoCount} Videos`}
              icon={PiFilesDuotone}
              colorClass="bg-indigo-500"
              link="/dashboard/myspace"
            />
            <StatsCard
              title="Quota Usage"
              value={`${totalSizePercent.toFixed(1)}%`}
              subtext={`Using ${totalSizePercent.toFixed(1)}% of your free tier`}
              icon={PiChartPieSliceDuotone}
              colorClass="bg-purple-500"
              link="/dashboard/myspace"
            />
          </>
        )}
      </div>

      {/* Mobile App & Enhancement Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 shrink-0 pb-8">
        {/* Mobile App Download Section */}
        <div className="bg-gradient-to-br lg:col-span-2 from-green-50 via-emerald-50 to-teal-50 p-6 lg:p-8 rounded-3xl border border-green-200 relative overflow-hidden group shadow-sm hover:shadow-md transition-shadow">
          <div className="absolute -right-20 -top-20 w-52 h-52 bg-green-300/20 rounded-full blur-3xl group-hover:bg-green-300/30 transition-all duration-500"></div>
          <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-teal-300/15 rounded-full blur-2xl"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-green-500 bg-opacity-20">
                <CiMobile3 className="text-3xl text-green-600" />
              </div>
              <span className="text-xs font-bold text-green-700 bg-green-200/50 px-3 py-1 rounded-full">NEW</span>
            </div>

            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">Take Cloud Point Everywhere</h2>
            <p className="text-gray-700 text-sm lg:text-base mb-4 leading-relaxed font-medium">
              Download our mobile app and access your files anytime, anywhere. Manage your cloud storage with the same ease on your phone.
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                <div className="w-1.5 h-1.5 rounded-full bg-green-600"></div>
                <span>Instant access to all your files</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                <div className="w-1.5 h-1.5 rounded-full bg-green-600"></div>
                <span>Upload photos directly from your gallery</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                <div className="w-1.5 h-1.5 rounded-full bg-green-600"></div>
                <span>Offline viewing & management</span>
              </div>
            </div>

            <a
              href="/cloud_point.apk"
              download="cloud_point.apk"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-green-200 hover:shadow-green-300 hover:from-green-700 hover:to-emerald-700 transition-all active:scale-95 text-sm lg:text-base group/btn">
              <PiDownloadSimpleBold className="text-lg group-hover/btn:animate-bounce" />
              Download APK
            </a>
          </div>
        </div>
      </div>

      {/* Quick Tips Section */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 shrink-0 pb-8">
        <div className="bg-[#0f172a] p-6 lg:p-8 rounded-3xl text-white relative overflow-hidden group">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all duration-500"></div>
          <h2 className="text-xl font-bold mb-3 relative z-10 text-white">Pro Tip: Image Editing</h2>
          <p className="text-slate-400 text-sm mb-6 leading-relaxed relative z-10 max-w-sm">
            Did you know you can crop and rotate your images directly inside Cloud Point? Head over to the Image Editor to try it out!
          </p>
          <button 
            onClick={() => navigate('/dashboard/editor')}
            className="px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold transition-all border border-white/10 relative z-10"
          >
            Try Editor
          </button>
        </div>
        <div className="bg-blue-50 p-6 lg:p-8 rounded-3xl border border-blue-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="max-w-xs">
            <h2 className="text-blue-900 text-xl font-bold mb-2">Need more space?</h2>
            <p className="text-blue-700/70 text-[13px] leading-relaxed font-medium">
              Upgrade to our premium plan and get up to 50GB of secure cloud storage.
            </p>
          </div>
          <button 
            onClick={() => navigate('/pricing')}
            className="shrink-0 w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 text-sm lg:text-base"
          >
            Upgrade Now
          </button>
        </div>
      </div> */}
    </div >
  );
}

export default Home;
